/**
 * Margine di massima del preventivo (deciso il 26/09/2026): non è il controllo
 * di gestione, serve ai commerciali per restare in un intervallo accettabile.
 *
 * - margine = vendita netta − costo;
 * - margine % = margine ÷ vendita netta, cioè prezzo = costo ÷ (1 − margine);
 * - semaforo: verde dalla soglia verde in su, giallo dalla gialla, rosso sotto,
 *   deciso sulla percentuale come si legge (un decimale);
 * - «per il verde»: il prezzo netto che dà la soglia verde, costo ÷ (1 − verde),
 *   arrotondato al centesimo per eccesso.
 */
import { fromCents, toCents, toThousandths } from './money.js';
export const DEFAULT_THRESHOLDS = { green: 40, yellow: 30 };
/** Il colore del semaforo per una percentuale già arrotondata a un decimale. */
export function marginLight(percent, thresholds) {
    if (percent >= thresholds.green)
        return 'green';
    if (percent >= thresholds.yellow)
        return 'yellow';
    return 'red';
}
/** Il prezzo netto che dà la soglia verde, al centesimo per eccesso. */
export function greenPrice(cost, thresholds) {
    const costCents = toCents(cost);
    if (thresholds.green >= 100)
        return fromCents(costCents);
    return fromCents(Math.ceil((costCents * 100) / (100 - thresholds.green)));
}
export function marginOf(sold, cost, thresholds) {
    const soldCents = toCents(sold);
    const costCents = toCents(cost);
    const marginCents = soldCents - costCents;
    const percent = soldCents > 0 ? Math.round((marginCents * 1000) / soldCents) / 10 : null;
    return {
        sold: fromCents(soldCents),
        cost: fromCents(costCents),
        margin: fromCents(marginCents),
        percent,
        light: percent === null ? null : marginLight(percent, thresholds),
        greenPrice: greenPrice(costCents / 100, thresholds),
    };
}
/** Le soglie dalla tabella Soglie di margine: voci VERDE e GIALLO, colonna «percent». */
export function thresholdsFrom(entries) {
    const value = (code, fallback) => {
        const found = Number(entries.find((entry) => entry.code === code)?.values.percent);
        return Number.isFinite(found) && found > 0 ? found : fallback;
    };
    return {
        green: value('VERDE', DEFAULT_THRESHOLDS.green),
        yellow: value('GIALLO', DEFAULT_THRESHOLDS.yellow),
    };
}
/**
 * An estimated cost: quantity × unit cost, to the cent (half up); times the
 * editions of its line (2.49), since every edition has the same cost.
 */
export function costTotal(quantity, unitCost, editions = 1) {
    return fromCents(Math.round((editions * toThousandths(quantity) * toCents(unitCost)) / 1000));
}
/**
 * A fee as a percentage of the taxable amount of the quote (proposal Listini
 * §6), rounded to the cent: 4 % of 11.750,00 is 470,00.
 */
export function feeAmount(percent, taxable) {
    return fromCents(Math.round((toCents(taxable) * toThousandths(percent)) / 100_000));
}
/**
 * Margins of the quote (decisions of 26 and 28/09/2026): every line on the sum
 * of its costs, and the total on the lines that have costs plus the transversal
 * costs (fees, travels), which belong to no line and weigh on the total only.
 * A line without costs has no margin and stays out of the total.
 */
export function quoteMargins(lines, transversal, thresholds) {
    const sum = (values) => values.reduce((cents, value) => cents + toCents(value), 0);
    let soldCents = 0;
    let costCents = sum(transversal);
    const margins = lines.map((line) => {
        if (line.costs.length === 0)
            return null;
        const cost = sum(line.costs);
        soldCents += toCents(line.sold);
        costCents += cost;
        return marginOf(line.sold, cost / 100, thresholds);
    });
    const costed = margins.some((margin) => margin !== null) || transversal.length > 0;
    return {
        lines: margins,
        total: costed ? marginOf(soldCents / 100, costCents / 100, thresholds) : null,
    };
}
//# sourceMappingURL=margins.js.map